import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Loader2, CheckCircle, X, Eye, Mail, Phone, Globe } from 'lucide-react';
import Cookies from 'js-cookie';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ListingRequests() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);

  useEffect(() => {
    fetchListingRequests();
  }, []);

  const fetchListingRequests = async () => {
    try {
      const token = Cookies.get('admin_token');
      const response = await axios.get(`${API}/startups/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Filter only listing requests
      const listingRequests = (response.data.data || []).filter(
        startup => startup.status === 'Listing Request'
      );
      
      setRequests(listingRequests);
    } catch (err) {
      toast.error('Failed to load listing requests');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (id, name) => {
    if (!window.confirm(`Accept listing request for "${name}"?\n\nThis will move the startup to the active Startups list.`)) {
      return;
    }

    try {
      const token = Cookies.get('admin_token');
      await axios.post(`${API}/startups/${id}/accept`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Listing request accepted successfully!');
      fetchListingRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept listing request');
    }
  };

  const handleReject = async (id, name) => {
    if (!window.confirm(`Reject listing request for "${name}"?\n\nThis will permanently delete the submission.`)) {
      return;
    }

    try {
      const token = Cookies.get('admin_token');
      await axios.delete(`${API}/startups/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Listing request rejected');
      fetchListingRequests();
    } catch (err) {
      toast.error('Failed to reject listing request');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading mb-2">Listing Requests</h1>
          <p className="text-muted-foreground">Review and approve startup submissions from the homepage</p>
        </div>
        <Badge variant="secondary" className="text-lg px-4 py-2">
          {requests.length} Pending
        </Badge>
      </div>

      {requests.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="mb-2">No pending listing requests</p>
              <p className="text-sm">New submissions from the homepage will appear here</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {requests.map((request) => (
            <Card key={request.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start gap-4">
                  {request.logo && (
                    <img 
                      src={request.logo} 
                      alt={request.startup_name}
                      className="w-16 h-16 object-contain rounded-lg border p-2"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-xl mb-1">{request.startup_name}</CardTitle>
                    <CardDescription>
                      {request.industry || 'Industry not specified'}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Quick Info */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {request.founding_year && (
                    <div>
                      <p className="text-muted-foreground">Founded</p>
                      <p className="font-medium">{request.founding_year}</p>
                    </div>
                  )}
                  {request.employee_count_range && (
                    <div>
                      <p className="text-muted-foreground">Employees</p>
                      <p className="font-medium">{request.employee_count_range}</p>
                    </div>
                  )}
                  {(request.city_name || request.country_name) && (
                    <div className="col-span-2">
                      <p className="text-muted-foreground">Location</p>
                      <p className="font-medium">
                        {[request.city_name, request.state_name, request.country_name]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                    </div>
                  )}
                </div>

                {/* Contact Info */}
                <div className="pt-3 border-t space-y-2 text-sm">
                  {request.contact_email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <a 
                        href={`mailto:${request.contact_email}`}
                        className="text-primary hover:underline"
                      >
                        {request.contact_email}
                      </a>
                    </div>
                  )}
                  {request.contact_phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{request.contact_country_code} {request.contact_phone}</span>
                    </div>
                  )}
                  {request.website_url && (
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                      <a 
                        href={request.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {request.website_url}
                      </a>
                    </div>
                  )}
                </div>

                {/* Founders */}
                {request.founders && request.founders.length > 0 && (
                  <div className="pt-3 border-t">
                    <p className="text-sm font-medium mb-2">Founders</p>
                    <div className="space-y-2">
                      {request.founders.map((founder, index) => (
                        <div key={index} className="text-sm">
                          <p className="font-medium">{founder.name || 'N/A'}</p>
                          {founder.designation && (
                            <p className="text-muted-foreground text-xs">{founder.designation}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Submission Date */}
                <div className="pt-3 border-t text-xs text-muted-foreground">
                  Submitted on {new Date(request.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-3 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/admin/startups/${request.id}`)}
                    className="flex-1"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View Details
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleAccept(request.id, request.startup_name)}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleReject(request.id, request.startup_name)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
